import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { axiosInstance } from "../../config";
import toast from "react-hot-toast";
import { SiX, SiTiktok, SiYoutube, SiFacebook } from "react-icons/si";
import "./editScheduledPostModal.css";

// The platforms services/socialPosting.js can post to. Other connections
// (Twitch, Kick) are stream sources, not upload targets.
const PLATFORM_INFO = {
  twitter: { name: "X", Icon: SiX, color: "#000" },
  tiktok: { name: "TikTok", Icon: SiTiktok, color: "#000" },
  youtube: { name: "YouTube", Icon: SiYoutube, color: "#FF0000" },
  facebook: { name: "Facebook", Icon: SiFacebook, color: "#1877F2" },
};

const EditScheduledPostModal = ({ post, onClose, onUpdated, onDeleted }) => {
  const { t } = useTranslation('schedule');
  const [caption, setCaption] = useState(post?.caption || "");
  const [scheduledTime, setScheduledTime] = useState("");
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [connectedPlatforms, setConnectedPlatforms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (post) {
      setCaption(post.caption || "");
      setScheduledTime(formatDateTimeLocal(new Date(post.scheduledTime)));
      setSelectedPlatforms(post.platforms?.map(p => p.name) || []);
    }
    fetchConnectedPlatforms();
  }, [post]);

  const formatDateTimeLocal = (date) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const fetchConnectedPlatforms = async () => {
    try {
      const res = await axiosInstance.get("/users/me/creator-settings");
      const connections = res.data.socialConnections || {};
      const connected = Object.entries(connections)
        .filter(([platform, data]) => data.connected && PLATFORM_INFO[platform])
        .map(([platform]) => platform);
      setConnectedPlatforms(connected);
    } catch (err) {
      console.error("Failed to fetch platforms:", err);
    }
  };

  const togglePlatform = (platform) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform)
        ? prev.filter((p) => p !== platform)
        : [...prev, platform]
    );
  };

  const handleSave = async () => {
    if (selectedPlatforms.length === 0) {
      return toast.error(t('selectAtLeastOnePlatform'));
    }

    const scheduleDate = new Date(scheduledTime);
    if (scheduleDate <= new Date()) {
      return toast.error(t('selectFutureTime'));
    }

    setSaving(true);
    try {
      await axiosInstance.put(`/scheduled-posts/${post._id}`, {
        caption,
        scheduledTime: scheduleDate.toISOString(),
        platforms: selectedPlatforms,
      });
      toast.success(t('changesSaved'));
      onUpdated?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || t('failedToSaveChanges'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axiosInstance.delete(`/scheduled-posts/${post._id}`);
      toast.success(t('postCancelled'));
      onDeleted?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || t('failedToCancelPost'));
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const minDateTime = formatDateTimeLocal(new Date());

  // Format the scheduled time for display
  const formatDisplayTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const timeStr = date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

    if (date.toDateString() === now.toDateString()) {
      return `${t('todayAt')} ${timeStr}`;
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return `${t('tomorrowAt')} ${timeStr}`;
    } else {
      return date.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" }) + ` at ${timeStr}`;
    }
  };

  if (!post) return null;

  return (
    <div className="edit-post-overlay" onClick={onClose}>
      <div className="edit-post-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header with clip title */}
        <div className="edit-post-header">
          <div className="edit-post-header-content">
            <span className="edit-post-label">{t('scheduledPost')}</span>
            <h3 className="edit-post-title">{post.clipTitle || t('untitledClip')}</h3>
          </div>
          <button className="edit-post-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Delete Confirmation */}
        {showDeleteConfirm ? (
          <div className="edit-post-delete-confirm">
            <div className="edit-post-delete-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h4>{t('cancelScheduledPost')}</h4>
            <p>{t('clipWontBePosted')}</p>
            <div className="edit-post-delete-actions">
              <button
                className="edit-post-delete-cancel"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                {t('keepIt')}
              </button>
              <button
                className="edit-post-delete-confirm-btn"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? t('cancelling') : t('yesCancelPost')}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Scheduled Time - Most prominent */}
            <div className="edit-post-section edit-post-time-section">
              <div className="edit-post-time-display">
                <div className="edit-post-time-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <span className="edit-post-time-text">
                  {formatDisplayTime(scheduledTime)}
                </span>
              </div>
              <input
                type="datetime-local"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                min={minDateTime}
                className="edit-post-datetime"
              />
            </div>

            {/* Platforms */}
            <div className="edit-post-section">
              <label className="edit-post-section-label">{t('postingTo')}</label>
              <div className="edit-post-platforms">
                {connectedPlatforms.map((platform) => {
                  const info = PLATFORM_INFO[platform];
                  const isSelected = selectedPlatforms.includes(platform);
                  return (
                    <button
                      key={platform}
                      type="button"
                      className={`edit-post-platform ${isSelected ? "selected" : ""}`}
                      onClick={() => togglePlatform(platform)}
                      title={info.name}
                      aria-label={info.name}
                    >
                      <span
                        className="edit-post-platform-icon"
                        style={{ backgroundColor: info.color }}
                      >
                        <info.Icon />
                      </span>
                      {isSelected && (
                        <span className="edit-post-platform-check">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Caption */}
            <div className="edit-post-section">
              <label className="edit-post-section-label">{t('caption')}</label>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                maxLength={500}
                placeholder={t('writeCaptionShort')}
                className="edit-post-caption"
                rows={3}
              />
              <span className="edit-post-caption-count">{caption.length}/500</span>
            </div>

            {/* Actions */}
            <div className="edit-post-actions">
              <button
                className="edit-post-delete-btn"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={saving}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
                {t('cancelPost')}
              </button>
              <button
                className="edit-post-save-btn"
                onClick={handleSave}
                disabled={saving || selectedPlatforms.length === 0}
              >
                {saving ? (
                  <>
                    <span className="edit-post-spinner" />
                    {t('common:saving')}
                  </>
                ) : (
                  t('saveChanges')
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EditScheduledPostModal;
